import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAutoId } from '@/lib/hooks';

interface FieldContextValue {
  id: string;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export interface FieldControlProps {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  required?: boolean;
}

/**
 * Every control in this library calls this so that dropping it inside a `<Field>`
 * wires up the label, the helper text and the error message automatically, while
 * still working standalone.
 */
export function useFieldControl(overrides: FieldControlProps = {}): FieldControlProps {
  const ctx = useContext(FieldContext);
  if (!ctx) return overrides;
  const describedBy = [overrides['aria-describedby'], ctx.describedBy].filter(Boolean).join(' ');
  return {
    id: overrides.id ?? ctx.id,
    'aria-describedby': describedBy || undefined,
    'aria-invalid': overrides['aria-invalid'] ?? (ctx.invalid ? true : undefined),
    required: overrides.required ?? (ctx.required || undefined),
  };
}

export interface FieldProps {
  label: ReactNode;
  children: ReactNode;
  /** Supply when the control has its own id; otherwise one is generated. */
  id?: string;
  required?: boolean;
  /** Persistent helper text. Hidden while an error is showing. */
  hint?: ReactNode;
  /** Error message, rendered below the control and announced. */
  error?: ReactNode;
  /** Trailing element on the label row, e.g. an "Esqueci minha senha" link. */
  action?: ReactNode;
  hideLabel?: boolean;
  className?: string;
}

export function Field({
  label,
  children,
  id,
  required = false,
  hint,
  error,
  action,
  hideLabel = false,
  className,
}: FieldProps) {
  const autoId = useAutoId('field');
  const controlId = id ?? autoId;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <FieldContext.Provider
      value={{ id: controlId, describedBy: describedBy || undefined, invalid: Boolean(error), required }}
    >
      <div className={cn('flex flex-col gap-1.5', className)}>
        <div className="flex items-baseline justify-between gap-3">
          <label
            htmlFor={controlId}
            className={cn(
              'text-xs font-semibold uppercase tracking-wide text-text-muted',
              hideLabel && 'sr-only',
            )}
          >
            {label}
            {required ? (
              <span className="ml-1 text-primary" aria-hidden="true">
                *
              </span>
            ) : null}
            {required ? <span className="sr-only"> (obrigatório)</span> : null}
          </label>
          {action ? <div className="shrink-0 text-xs">{action}</div> : null}
        </div>

        {children}

        {error ? (
          <p
            id={errorId}
            role="alert"
            className="flex items-start gap-1.5 text-sm font-medium text-loss"
          >
            <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <span>{error}</span>
          </p>
        ) : hint ? (
          <p id={hintId} className="text-sm text-text-faint">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
